// @ts-ignore
import { hasProp, assert } from "@utils/assertion";
// @ts-ignore
import { isArray } from "@utils/arrayUtils";
// @ts-ignore
import { parseJsonWithNumbersAsBigint, renderJsonWithNumbersAsBigint, type ParserResult } from "@utils/json";
// @ts-ignore
import { toBase64 } from "@utils/blobUtils";
// @ts-ignore
import { DateTime } from "luxon";
// @ts-ignore
import { type ReadonlyUint8Array } from "@utils/arrayUtils";
import type { FormFilter, ContentMetadata, ChannelMetadata, CustomPageListView, FullSearchResults } from "../schema";
import { serialize_FormFilter_internal, deserialize_FormFilter_internal } from "./FormFilter";
import { serialize_ContentMetadata_internal, deserialize_ContentMetadata_internal } from "./ContentMetadata";
import { serialize_ChannelMetadata_internal, deserialize_ChannelMetadata_internal } from "./ChannelMetadata";
import { serialize_CustomPageListView_internal, deserialize_CustomPageListView_internal } from "./CustomPageListView";

export function serialize_FullSearchResults_internal(value: FullSearchResults): unknown {
  return ((value: {
    actionableFormFilters: FormFilter[];
    actionables: ContentMetadata[];
    channelPaging: {
      page: number;
      totalCount: bigint;
    };
    channels: ChannelMetadata[];
    customPages: CustomPageListView[];
    downloadSourceParams: { additionalProperties: Record<string, string> };
    summaries: ContentMetadata[];
    summariesPaging: {
      page: number;
      totalCount: bigint;
    };
    summaryFormFilters: FormFilter[];
    verifaiFormFilters: FormFilter[];
    verifaiPaging: {
      page: number;
      totalCount: bigint;
    };
    verifais: ContentMetadata[];
  }): unknown => {
    return {
      actionableFormFilters: ((value: FormFilter[]): unknown => {
        return value.map((value: FormFilter): unknown => {
          return serialize_FormFilter_internal(value);
        });
      })(value["actionableFormFilters"]),
      actionables: ((value: ContentMetadata[]): unknown => {
        return value.map((value: ContentMetadata): unknown => {
          return serialize_ContentMetadata_internal(value);
        });
      })(value["actionables"]),
      channelPaging: ((value: { page: number; totalCount: bigint }): unknown => {
        return {
          page: ((value: number): unknown => {
            return value;
          })(value["page"]),
          totalCount: ((value: bigint): unknown => {
            return value;
          })(value["totalCount"]),
        };
      })(value["channelPaging"]),
      channels: ((value: ChannelMetadata[]): unknown => {
        return value.map((value: ChannelMetadata): unknown => {
          return serialize_ChannelMetadata_internal(value);
        });
      })(value["channels"]),
      customPages: ((value: CustomPageListView[]): unknown => {
        return value.map((value: CustomPageListView): unknown => {
          return serialize_CustomPageListView_internal(value);
        });
      })(value["customPages"]),
      downloadSourceParams: ((value: { additionalProperties: Record<string, string> }): unknown => {
        const unordered_obj: Record<string, unknown> = {
          ...Object.fromEntries(
            Object.entries(value.additionalProperties).map(([propertyKey, propertyValue]) => {
              return [
                propertyKey,
                ((value: string): unknown => {
                  return value;
                })(propertyValue),
              ];
            }),
          ),
        };
        return Object.keys(unordered_obj)
          .sort()
          .reduce(
            (obj, key) => {
              obj[key] = unordered_obj[key];
              return obj;
            },
            {} as Record<string, unknown>,
          );
      })(value["downloadSourceParams"]),
      summaries: ((value: ContentMetadata[]): unknown => {
        return value.map((value: ContentMetadata): unknown => {
          return serialize_ContentMetadata_internal(value);
        });
      })(value["summaries"]),
      summariesPaging: ((value: { page: number; totalCount: bigint }): unknown => {
        return {
          page: ((value: number): unknown => {
            return value;
          })(value["page"]),
          totalCount: ((value: bigint): unknown => {
            return value;
          })(value["totalCount"]),
        };
      })(value["summariesPaging"]),
      summaryFormFilters: ((value: FormFilter[]): unknown => {
        return value.map((value: FormFilter): unknown => {
          return serialize_FormFilter_internal(value);
        });
      })(value["summaryFormFilters"]),
      verifaiFormFilters: ((value: FormFilter[]): unknown => {
        return value.map((value: FormFilter): unknown => {
          return serialize_FormFilter_internal(value);
        });
      })(value["verifaiFormFilters"]),
      verifaiPaging: ((value: { page: number; totalCount: bigint }): unknown => {
        return {
          page: ((value: number): unknown => {
            return value;
          })(value["page"]),
          totalCount: ((value: bigint): unknown => {
            return value;
          })(value["totalCount"]),
        };
      })(value["verifaiPaging"]),
      verifais: ((value: ContentMetadata[]): unknown => {
        return value.map((value: ContentMetadata): unknown => {
          return serialize_ContentMetadata_internal(value);
        });
      })(value["verifais"]),
    };
  })(value);
}
export function serialize_FullSearchResults(obj: FullSearchResults): string {
  return renderJsonWithNumbersAsBigint(serialize_FullSearchResults_internal(obj));
}
export function deserialize_FullSearchResults_internal(value: unknown): ParserResult<FullSearchResults> {
  return ((
    value: unknown,
  ): ParserResult<{
    actionableFormFilters: FormFilter[];
    actionables: ContentMetadata[];
    channelPaging: {
      page: number;
      totalCount: bigint;
    };
    channels: ChannelMetadata[];
    customPages: CustomPageListView[];
    downloadSourceParams: { additionalProperties: Record<string, string> };
    summaries: ContentMetadata[];
    summariesPaging: {
      page: number;
      totalCount: bigint;
    };
    summaryFormFilters: FormFilter[];
    verifaiFormFilters: FormFilter[];
    verifaiPaging: {
      page: number;
      totalCount: bigint;
    };
    verifais: ContentMetadata[];
  }> => {
    if (typeof value !== "object") {
      return { success: false, reason: `[#/components/schemas/FullSearchResults]: '${value}' expected to be of type 'object'.` };
    }
    if (value === null) {
      return { success: false, reason: `[#/components/schemas/FullSearchResults]: '${value}' expected to not be null.` };
    }
    if (!hasProp(value, "actionableFormFilters")) {
      return { success: false, reason: `[#/components/schemas/FullSearchResults/actionableFormFilters]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'actionableFormFilters'.` };
    }
    const parsed_actionableFormFilters = ((): ParserResult<FormFilter[]> => {
      return ((value: unknown): ParserResult<FormFilter[]> => {
        if (!isArray(value)) {
          return { success: false, reason: `[#/components/schemas/FullSearchResults/actionableFormFilters]: '${value}' expected to be an array.` };
        }
        const parsed = value.map((value: unknown): ParserResult<FormFilter> => {
          return deserialize_FormFilter_internal(value);
        });
        if (parsed.find((value) => !value.success) !== undefined) {
          return {
            success: false,
            reason: `[#/components/schemas/FullSearchResults/actionableFormFilters]: Reason(s): '${parsed
              .filter((p) => !p.success)
              .map((p) => p.reason)
              .join(", ")}'.`,
          };
        }
        return {
          success: true,
          value: parsed.map((entry) => {
            assert(entry.success == true);
            return entry.value;
          }),
        };
      })(value["actionableFormFilters"]);
    })();
    if (!parsed_actionableFormFilters.success) {
      return { success: false, reason: parsed_actionableFormFilters.reason };
    }
    if (!hasProp(value, "actionables")) {
      return { success: false, reason: `[#/components/schemas/FullSearchResults/actionables]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'actionables'.` };
    }
    const parsed_actionables = ((): ParserResult<ContentMetadata[]> => {
      return ((value: unknown): ParserResult<ContentMetadata[]> => {
        if (!isArray(value)) {
          return { success: false, reason: `[#/components/schemas/FullSearchResults/actionables]: '${value}' expected to be an array.` };
        }
        const parsed = value.map((value: unknown): ParserResult<ContentMetadata> => {
          return deserialize_ContentMetadata_internal(value);
        });
        if (parsed.find((value) => !value.success) !== undefined) {
          return {
            success: false,
            reason: `[#/components/schemas/FullSearchResults/actionables]: Reason(s): '${parsed
              .filter((p) => !p.success)
              .map((p) => p.reason)
              .join(", ")}'.`,
          };
        }
        return {
          success: true,
          value: parsed.map((entry) => {
            assert(entry.success == true);
            return entry.value;
          }),
        };
      })(value["actionables"]);
    })();
    if (!parsed_actionables.success) {
      return { success: false, reason: parsed_actionables.reason };
    }
    if (!hasProp(value, "channelPaging")) {
      return { success: false, reason: `[#/components/schemas/FullSearchResults/channelPaging]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'channelPaging'.` };
    }
    const parsed_channelPaging = ((): ParserResult<{
      page: number;
      totalCount: bigint;
    }> => {
      return ((
        value: unknown,
      ): ParserResult<{
        page: number;
        totalCount: bigint;
      }> => {
        if (typeof value !== "object") {
          return { success: false, reason: `[#/components/schemas/FullSearchResults/channelPaging]: '${value}' expected to be of type 'object'.` };
        }
        if (value === null) {
          return { success: false, reason: `[#/components/schemas/FullSearchResults/channelPaging]: '${value}' expected to not be null.` };
        }
        if (!hasProp(value, "page")) {
          return { success: false, reason: `[#/components/schemas/FullSearchResults/channelPaging/page]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'page'.` };
        }
        const parsed_page = ((): ParserResult<number> => {
          return ((value: unknown): ParserResult<number> => {
            if (typeof value === "number") {
              return { success: true, value: value };
            }
            return { success: false, reason: `[#/components/schemas/FullSearchResults/channelPaging/page]: '${value}' expected to be of type number.` };
          })(value["page"]);
        })();
        if (!parsed_page.success) {
          return { success: false, reason: parsed_page.reason };
        }
        if (!hasProp(value, "totalCount")) {
          return { success: false, reason: `[#/components/schemas/FullSearchResults/channelPaging/totalCount]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'totalCount'.` };
        }
        const parsed_totalCount = ((): ParserResult<bigint> => {
          return ((value: unknown): ParserResult<bigint> => {
            if (typeof value === "number") {
              return { success: true, value: BigInt(value) };
            }
            if (typeof value === "bigint") {
              return { success: true, value: value };
            }
            return { success: false, reason: `[#/components/schemas/FullSearchResults/channelPaging/totalCount]: '${value}' expected to be of type bigint or number.` };
          })(value["totalCount"]);
        })();
        if (!parsed_totalCount.success) {
          return { success: false, reason: parsed_totalCount.reason };
        }
        return { success: true, value: { page: parsed_page.value, totalCount: parsed_totalCount.value } };
      })(value["channelPaging"]);
    })();
    if (!parsed_channelPaging.success) {
      return { success: false, reason: parsed_channelPaging.reason };
    }
    if (!hasProp(value, "channels")) {
      return { success: false, reason: `[#/components/schemas/FullSearchResults/channels]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'channels'.` };
    }
    const parsed_channels = ((): ParserResult<ChannelMetadata[]> => {
      return ((value: unknown): ParserResult<ChannelMetadata[]> => {
        if (!isArray(value)) {
          return { success: false, reason: `[#/components/schemas/FullSearchResults/channels]: '${value}' expected to be an array.` };
        }
        const parsed = value.map((value: unknown): ParserResult<ChannelMetadata> => {
          return deserialize_ChannelMetadata_internal(value);
        });
        if (parsed.find((value) => !value.success) !== undefined) {
          return {
            success: false,
            reason: `[#/components/schemas/FullSearchResults/channels]: Reason(s): '${parsed
              .filter((p) => !p.success)
              .map((p) => p.reason)
              .join(", ")}'.`,
          };
        }
        return {
          success: true,
          value: parsed.map((entry) => {
            assert(entry.success == true);
            return entry.value;
          }),
        };
      })(value["channels"]);
    })();
    if (!parsed_channels.success) {
      return { success: false, reason: parsed_channels.reason };
    }
    if (!hasProp(value, "customPages")) {
      return { success: false, reason: `[#/components/schemas/FullSearchResults/customPages]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'customPages'.` };
    }
    const parsed_customPages = ((): ParserResult<CustomPageListView[]> => {
      return ((value: unknown): ParserResult<CustomPageListView[]> => {
        if (!isArray(value)) {
          return { success: false, reason: `[#/components/schemas/FullSearchResults/customPages]: '${value}' expected to be an array.` };
        }
        const parsed = value.map((value: unknown): ParserResult<CustomPageListView> => {
          return deserialize_CustomPageListView_internal(value);
        });
        if (parsed.find((value) => !value.success) !== undefined) {
          return {
            success: false,
            reason: `[#/components/schemas/FullSearchResults/customPages]: Reason(s): '${parsed
              .filter((p) => !p.success)
              .map((p) => p.reason)
              .join(", ")}'.`,
          };
        }
        return {
          success: true,
          value: parsed.map((entry) => {
            assert(entry.success == true);
            return entry.value;
          }),
        };
      })(value["customPages"]);
    })();
    if (!parsed_customPages.success) {
      return { success: false, reason: parsed_customPages.reason };
    }
    if (!hasProp(value, "downloadSourceParams")) {
      return { success: false, reason: `[#/components/schemas/FullSearchResults/downloadSourceParams]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'downloadSourceParams'.` };
    }
    const parsed_downloadSourceParams = ((): ParserResult<{ additionalProperties: Record<string, string> }> => {
      return ((value: unknown): ParserResult<{ additionalProperties: Record<string, string> }> => {
        if (typeof value !== "object") {
          return { success: false, reason: `[#/components/schemas/FullSearchResults/downloadSourceParams]: '${value}' expected to be of type 'object'.` };
        }
        if (value === null) {
          return { success: false, reason: `[#/components/schemas/FullSearchResults/downloadSourceParams]: '${value}' expected to not be null.` };
        }
        const parsed_additionalProperties = ((): ParserResult<Record<string, string>> => {
          const parsed = Object.entries(value).map(([key, value]): [string, ParserResult<string>] => {
            return [
              key,
              ((value: unknown): ParserResult<string> => {
                if (typeof value !== "string") {
                  return { success: false, reason: `[#/components/schemas/FullSearchResults/downloadSourceParams/additionalProperty]: '${value}' expected to be of type string.` };
                }
                return { success: true, value: value };
              })(value),
            ];
          });
          if (parsed.find(([_, value]) => !value.success) !== undefined) {
            return {
              success: false,
              reason: `[#/components/schemas/FullSearchResults/downloadSourceParams]: Reason(s): '${parsed
                .map(([_, value]) => value)
                .filter((p) => !p.success)
                .map((p) => p.reason)
                .join(", ")}'.`,
            };
          }
          return {
            success: true,
            value: Object.fromEntries(
              parsed.map(([key, value]) => {
                assert(value.success);
                return [key, value.value];
              }),
            ),
          };
        })();
        if (!parsed_additionalProperties.success) {
          return { success: false, reason: parsed_additionalProperties.reason };
        }
        return { success: true, value: { additionalProperties: parsed_additionalProperties.value } };
      })(value["downloadSourceParams"]);
    })();
    if (!parsed_downloadSourceParams.success) {
      return { success: false, reason: parsed_downloadSourceParams.reason };
    }
    if (!hasProp(value, "summaries")) {
      return { success: false, reason: `[#/components/schemas/FullSearchResults/summaries]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'summaries'.` };
    }
    const parsed_summaries = ((): ParserResult<ContentMetadata[]> => {
      return ((value: unknown): ParserResult<ContentMetadata[]> => {
        if (!isArray(value)) {
          return { success: false, reason: `[#/components/schemas/FullSearchResults/summaries]: '${value}' expected to be an array.` };
        }
        const parsed = value.map((value: unknown): ParserResult<ContentMetadata> => {
          return deserialize_ContentMetadata_internal(value);
        });
        if (parsed.find((value) => !value.success) !== undefined) {
          return {
            success: false,
            reason: `[#/components/schemas/FullSearchResults/summaries]: Reason(s): '${parsed
              .filter((p) => !p.success)
              .map((p) => p.reason)
              .join(", ")}'.`,
          };
        }
        return {
          success: true,
          value: parsed.map((entry) => {
            assert(entry.success == true);
            return entry.value;
          }),
        };
      })(value["summaries"]);
    })();
    if (!parsed_summaries.success) {
      return { success: false, reason: parsed_summaries.reason };
    }
    if (!hasProp(value, "summariesPaging")) {
      return { success: false, reason: `[#/components/schemas/FullSearchResults/summariesPaging]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'summariesPaging'.` };
    }
    const parsed_summariesPaging = ((): ParserResult<{
      page: number;
      totalCount: bigint;
    }> => {
      return ((
        value: unknown,
      ): ParserResult<{
        page: number;
        totalCount: bigint;
      }> => {
        if (typeof value !== "object") {
          return { success: false, reason: `[#/components/schemas/FullSearchResults/summariesPaging]: '${value}' expected to be of type 'object'.` };
        }
        if (value === null) {
          return { success: false, reason: `[#/components/schemas/FullSearchResults/summariesPaging]: '${value}' expected to not be null.` };
        }
        if (!hasProp(value, "page")) {
          return { success: false, reason: `[#/components/schemas/FullSearchResults/summariesPaging/page]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'page'.` };
        }
        const parsed_page = ((): ParserResult<number> => {
          return ((value: unknown): ParserResult<number> => {
            if (typeof value === "number") {
              return { success: true, value: value };
            }
            return { success: false, reason: `[#/components/schemas/FullSearchResults/summariesPaging/page]: '${value}' expected to be of type number.` };
          })(value["page"]);
        })();
        if (!parsed_page.success) {
          return { success: false, reason: parsed_page.reason };
        }
        if (!hasProp(value, "totalCount")) {
          return { success: false, reason: `[#/components/schemas/FullSearchResults/summariesPaging/totalCount]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'totalCount'.` };
        }
        const parsed_totalCount = ((): ParserResult<bigint> => {
          return ((value: unknown): ParserResult<bigint> => {
            if (typeof value === "number") {
              return { success: true, value: BigInt(value) };
            }
            if (typeof value === "bigint") {
              return { success: true, value: value };
            }
            return { success: false, reason: `[#/components/schemas/FullSearchResults/summariesPaging/totalCount]: '${value}' expected to be of type bigint or number.` };
          })(value["totalCount"]);
        })();
        if (!parsed_totalCount.success) {
          return { success: false, reason: parsed_totalCount.reason };
        }
        return { success: true, value: { page: parsed_page.value, totalCount: parsed_totalCount.value } };
      })(value["summariesPaging"]);
    })();
    if (!parsed_summariesPaging.success) {
      return { success: false, reason: parsed_summariesPaging.reason };
    }
    if (!hasProp(value, "summaryFormFilters")) {
      return { success: false, reason: `[#/components/schemas/FullSearchResults/summaryFormFilters]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'summaryFormFilters'.` };
    }
    const parsed_summaryFormFilters = ((): ParserResult<FormFilter[]> => {
      return ((value: unknown): ParserResult<FormFilter[]> => {
        if (!isArray(value)) {
          return { success: false, reason: `[#/components/schemas/FullSearchResults/summaryFormFilters]: '${value}' expected to be an array.` };
        }
        const parsed = value.map((value: unknown): ParserResult<FormFilter> => {
          return deserialize_FormFilter_internal(value);
        });
        if (parsed.find((value) => !value.success) !== undefined) {
          return {
            success: false,
            reason: `[#/components/schemas/FullSearchResults/summaryFormFilters]: Reason(s): '${parsed
              .filter((p) => !p.success)
              .map((p) => p.reason)
              .join(", ")}'.`,
          };
        }
        return {
          success: true,
          value: parsed.map((entry) => {
            assert(entry.success == true);
            return entry.value;
          }),
        };
      })(value["summaryFormFilters"]);
    })();
    if (!parsed_summaryFormFilters.success) {
      return { success: false, reason: parsed_summaryFormFilters.reason };
    }
    if (!hasProp(value, "verifaiFormFilters")) {
      return { success: false, reason: `[#/components/schemas/FullSearchResults/verifaiFormFilters]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'verifaiFormFilters'.` };
    }
    const parsed_verifaiFormFilters = ((): ParserResult<FormFilter[]> => {
      return ((value: unknown): ParserResult<FormFilter[]> => {
        if (!isArray(value)) {
          return { success: false, reason: `[#/components/schemas/FullSearchResults/verifaiFormFilters]: '${value}' expected to be an array.` };
        }
        const parsed = value.map((value: unknown): ParserResult<FormFilter> => {
          return deserialize_FormFilter_internal(value);
        });
        if (parsed.find((value) => !value.success) !== undefined) {
          return {
            success: false,
            reason: `[#/components/schemas/FullSearchResults/verifaiFormFilters]: Reason(s): '${parsed
              .filter((p) => !p.success)
              .map((p) => p.reason)
              .join(", ")}'.`,
          };
        }
        return {
          success: true,
          value: parsed.map((entry) => {
            assert(entry.success == true);
            return entry.value;
          }),
        };
      })(value["verifaiFormFilters"]);
    })();
    if (!parsed_verifaiFormFilters.success) {
      return { success: false, reason: parsed_verifaiFormFilters.reason };
    }
    if (!hasProp(value, "verifaiPaging")) {
      return { success: false, reason: `[#/components/schemas/FullSearchResults/verifaiPaging]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'verifaiPaging'.` };
    }
    const parsed_verifaiPaging = ((): ParserResult<{
      page: number;
      totalCount: bigint;
    }> => {
      return ((
        value: unknown,
      ): ParserResult<{
        page: number;
        totalCount: bigint;
      }> => {
        if (typeof value !== "object") {
          return { success: false, reason: `[#/components/schemas/FullSearchResults/verifaiPaging]: '${value}' expected to be of type 'object'.` };
        }
        if (value === null) {
          return { success: false, reason: `[#/components/schemas/FullSearchResults/verifaiPaging]: '${value}' expected to not be null.` };
        }
        if (!hasProp(value, "page")) {
          return { success: false, reason: `[#/components/schemas/FullSearchResults/verifaiPaging/page]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'page'.` };
        }
        const parsed_page = ((): ParserResult<number> => {
          return ((value: unknown): ParserResult<number> => {
            if (typeof value === "number") {
              return { success: true, value: value };
            }
            return { success: false, reason: `[#/components/schemas/FullSearchResults/verifaiPaging/page]: '${value}' expected to be of type number.` };
          })(value["page"]);
        })();
        if (!parsed_page.success) {
          return { success: false, reason: parsed_page.reason };
        }
        if (!hasProp(value, "totalCount")) {
          return { success: false, reason: `[#/components/schemas/FullSearchResults/verifaiPaging/totalCount]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'totalCount'.` };
        }
        const parsed_totalCount = ((): ParserResult<bigint> => {
          return ((value: unknown): ParserResult<bigint> => {
            if (typeof value === "number") {
              return { success: true, value: BigInt(value) };
            }
            if (typeof value === "bigint") {
              return { success: true, value: value };
            }
            return { success: false, reason: `[#/components/schemas/FullSearchResults/verifaiPaging/totalCount]: '${value}' expected to be of type bigint or number.` };
          })(value["totalCount"]);
        })();
        if (!parsed_totalCount.success) {
          return { success: false, reason: parsed_totalCount.reason };
        }
        return { success: true, value: { page: parsed_page.value, totalCount: parsed_totalCount.value } };
      })(value["verifaiPaging"]);
    })();
    if (!parsed_verifaiPaging.success) {
      return { success: false, reason: parsed_verifaiPaging.reason };
    }
    if (!hasProp(value, "verifais")) {
      return { success: false, reason: `[#/components/schemas/FullSearchResults/verifais]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'verifais'.` };
    }
    const parsed_verifais = ((): ParserResult<ContentMetadata[]> => {
      return ((value: unknown): ParserResult<ContentMetadata[]> => {
        if (!isArray(value)) {
          return { success: false, reason: `[#/components/schemas/FullSearchResults/verifais]: '${value}' expected to be an array.` };
        }
        const parsed = value.map((value: unknown): ParserResult<ContentMetadata> => {
          return deserialize_ContentMetadata_internal(value);
        });
        if (parsed.find((value) => !value.success) !== undefined) {
          return {
            success: false,
            reason: `[#/components/schemas/FullSearchResults/verifais]: Reason(s): '${parsed
              .filter((p) => !p.success)
              .map((p) => p.reason)
              .join(", ")}'.`,
          };
        }
        return {
          success: true,
          value: parsed.map((entry) => {
            assert(entry.success == true);
            return entry.value;
          }),
        };
      })(value["verifais"]);
    })();
    if (!parsed_verifais.success) {
      return { success: false, reason: parsed_verifais.reason };
    }
    return {
      success: true,
      value: {
        actionableFormFilters: parsed_actionableFormFilters.value,
        actionables: parsed_actionables.value,
        channelPaging: parsed_channelPaging.value,
        channels: parsed_channels.value,
        customPages: parsed_customPages.value,
        downloadSourceParams: parsed_downloadSourceParams.value,
        summaries: parsed_summaries.value,
        summariesPaging: parsed_summariesPaging.value,
        summaryFormFilters: parsed_summaryFormFilters.value,
        verifaiFormFilters: parsed_verifaiFormFilters.value,
        verifaiPaging: parsed_verifaiPaging.value,
        verifais: parsed_verifais.value,
      },
    };
  })(value);
}
export function deserialize_FullSearchResults(str: string): ParserResult<FullSearchResults> {
  return deserialize_FullSearchResults_internal(parseJsonWithNumbersAsBigint(str));
}
